import { deliveryFeeAPI, Listing } from '../api';

function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

type LocationRow = {
  latitude: number | null;
  longitude: number | null;
  distance_miles: number | null;
};

function resolveDistance(
  loc: LocationRow | undefined,
  userLat: number | undefined,
  userLng: number | undefined
): number | null {
  if (!loc) return null;
  if (loc.distance_miles != null && Number.isFinite(Number(loc.distance_miles))) {
    return Number(loc.distance_miles);
  }
  if (
    loc.latitude != null &&
    loc.longitude != null &&
    userLat != null &&
    userLng != null &&
    Number.isFinite(loc.latitude) &&
    Number.isFinite(loc.longitude)
  ) {
    return haversineMiles(userLat, userLng, loc.latitude, loc.longitude);
  }
  return null;
}

/** Filter homepage listings by each listing's max delivery radius (one batch API call). */
export async function filterListingsByDeliveryRange(
  listings: Listing[],
  userCoordinates: { lat: number; lng: number } | null,
  options?: { keepUserId?: number | null },
): Promise<{ listings: Listing[]; distances: Record<number, number> }> {
  if (!listings.length) return { listings: [], distances: {} };
  if (!userCoordinates) return { listings, distances: {} };

  const keepUserId =
    options?.keepUserId != null && Number(options.keepUserId) > 0
      ? Number(options.keepUserId)
      : null;

  try {
    const listingIds = listings.map((l) => Number(l.id)).filter((id) => id > 0);
    const locations = await deliveryFeeAPI.getListingLocationsBatch({
      listing_ids: listingIds,
      lat: userCoordinates.lat,
      lng: userCoordinates.lng,
    });
    const uLat = userCoordinates.lat;
    const uLng = userCoordinates.lng;
    const nextDistances: Record<number, number> = {};

    const inRangeListings = listings.filter((listing) => {
      const id = Number(listing.id);
      const loc =
        locations[id] ||
        (locations as Record<string, LocationRow>)[String(id)];
      const distance = resolveDistance(loc, uLat, uLng);
      if (distance != null) nextDistances[listing.id] = distance;

      // Sellers should still see their own listings after upload, even if the
      // saved delivery address is outside that listing's delivery radius.
      if (keepUserId != null && Number(listing.user_id) === keepUserId) {
        return true;
      }

      // Unknown distance → keep (don't hide listings that lack seller coords).
      if (distance == null) return true;
      const maxMiles = Math.max(Number(listing.max_delivery_miles) || 25, 25);
      return distance <= maxMiles;
    });

    return { listings: inRangeListings, distances: nextDistances };
  } catch (err) {
    console.warn('Delivery-range filter failed; showing unfiltered page', err);
    return { listings, distances: {} };
  }
}

/** Distances from an origin to each listing (one batch API call). */
export async function getListingDistances(
  listings: Listing[],
  origin: { lat: number; lng: number },
): Promise<Record<number, number>> {
  if (!listings.length) return {};

  const listingIds = listings.map((l) => Number(l.id)).filter((id) => id > 0);
  const locations = await deliveryFeeAPI.getListingLocationsBatch({
    listing_ids: listingIds,
    lat: origin.lat,
    lng: origin.lng,
  });

  const distances: Record<number, number> = {};
  for (const listing of listings) {
    const distance = resolveDistance(locations[listing.id], origin.lat, origin.lng);
    if (distance != null) distances[listing.id] = distance;
  }
  return distances;
}

/** Filter search results by a fixed mile radius from zip (one batch API call). */
export async function filterListingsByDistanceCap(
  listings: Listing[],
  origin: { lat: number; lng: number },
  maxMiles: number
): Promise<Listing[]> {
  if (!listings.length || maxMiles <= 0) return listings;

  const distances = await getListingDistances(listings, origin);
  return listings.filter((listing) => {
    const distance = distances[listing.id];
    return distance != null && distance <= maxMiles;
  });
}
